import { Contact, Search } from "lucide-react";
import * as React from "react";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip } from "@/components/ui/misc";
import { useColleagues } from "@/lib/queries";
import type { Colleague } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Choosing who to call: a colleague from a list, or a number typed in.
 *
 * One component because the choice is the same one on four surfaces — the phone page's Dial now
 * card, the dock's idle dialler, the dock's "Add a call" view, and the transfer box — and a rep
 * who has learnt that the people button is at the end of the field should not learn it again per
 * screen. It renders the *button and its panel*, not the field: the field belongs to whatever is
 * around it, which already owns the value, the placeholder and what Enter does.
 *
 * **Why a list at all.** A colleague's extension is four digits with no meaning, and the console
 * had nowhere to look one up — `/users` and `/endpoints` are supervisor-and-above, correctly,
 * because they carry email addresses, roles and SIP credentials. `GET /phone/directory` is the
 * rep-scoped answer, and this is what asks it.
 *
 * **Picking a colleague dials.** Every softphone worth using does, and the alternative — fill
 * the field, then find the Call button — is two deliberate acts for one intention. The parent
 * decides what "dial" means through `onPick`, so the transfer box transfers instead.
 *
 * **The search is local.** The list is one office in one SIP realm, it is already in memory, and
 * a server round trip per keystroke would make the common case (a dozen colleagues) slower than
 * scrolling. `features/endpoint-picker.tsx` pages and searches on the server for the opposite
 * reason: it lists every endpoint on the platform.
 */
export function DialTargetPicker({
  onPick, disabled = false, label = "Call a colleague",
}: {
  /** What to do with the chosen extension. The panel closes either way. */
  onPick: (extension: string) => void;
  disabled?: boolean;
  /** What the button announces. "Transfer to a colleague" on the transfer box. */
  label?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // Only once the panel is opened. A rep who never uses it — a setter working a dial list —
  // should not pay for a request on every page that renders a dialler.
  const { data, isPending, isError } = useColleagues({ enabled: open });

  // Reset between openings rather than on close: clearing while the panel is animating out
  // makes the list visibly re-populate as it fades.
  React.useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const rows = React.useMemo(() => match(data ?? [], search), [data, search]);

  const choose = (extension: string) => {
    setOpen(false);
    onPick(extension);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={label}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-10 shrink-0"
            disabled={disabled}
            aria-label={label}
          >
            <Contact />
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="border-b border-border p-1.5">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or extension"
            autoFocus
            autoComplete="off"
            className="h-8 text-sm"
            leading={<Search />}
            // Enter on a search box that has narrowed the list to one person means that person.
            // Anything else would submit the form the trigger sits in, which is the dialler —
            // and dial whatever half-typed thing is in ITS field.
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              const only = rows.length === 1 ? rows[0] : undefined;
              if (only) choose(only.extension);
            }}
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1" role="listbox" aria-label={label}>
          {isPending ? (
            <Empty>Loading…</Empty>
          ) : isError && !data ? (
            // A 404 here means the signed-in account has no phone, which every caller already
            // gates on — so this is a real failure and says so rather than showing an empty
            // list, which would read as "you work alone".
            <Empty>Could not load the directory.</Empty>
          ) : rows.length === 0 ? (
            <Empty>{search ? "Nobody matches." : "No colleagues to call."}</Empty>
          ) : (
            rows.map((c) => (
              <button
                key={c.extension}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => choose(c.extension)}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left",
                  "outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover",
                )}
              >
                {/* Not an availability promise: `null` is "the live-state store did not answer",
                    which `StatusDot` renders as its own thing rather than as offline. */}
                <StatusDot state={c.registered} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-text">
                    {c.name ?? `Extension ${c.extension}`}
                  </span>
                  {c.name && (
                    <span className="block font-mono text-xs text-text-muted">{c.extension}</span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-2 py-6 text-center text-xs text-text-muted">{children}</p>;
}

/**
 * The colleagues matching what was typed, in the order the server sent them.
 *
 * Substring and case-insensitive over the name AND the extension, because a rep half-remembers
 * one or the other — "Dana" or "10-something" — and a picker that only matched names would be
 * useless for the handsets that have none.
 *
 * Exported for its test: this is the one piece of behaviour here that is not React.
 */
export function match(colleagues: readonly Colleague[], search: string): Colleague[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...colleagues];
  return colleagues.filter(
    (c) =>
      c.extension.toLowerCase().includes(needle) ||
      (c.name ?? "").toLowerCase().includes(needle),
  );
}
